<template>
    <step-wrapper>
        <step-heading>
            <div class="flex flex-row items-center justify-start">
                <span class="flex-1">
                    {{ $t('BULK_ORDERING') }}
                </span>

                <span class="flex-none">
                    <button
                        v-if="isAvailable"

                        @click="toggleBulkOrder"
                        type="button"
                        role="switch"
                        aria-labelledby="bulkOrderLabel"
                        :aria-checked="isChecked"
                        :aria-label="isChecked ? $t('YES') : $t('NO')"
                        class="flex h-[60px] items-center rounded-full bg-gray-200 font-sans text-[16px] uppercase focus-outline-only"
                    >
                        <template v-if="isChecked">
                            <div class="rounded-full p-4 px-8">
                                {{ $t('NO') }}
                            </div>
                            <div class="rounded-full bg-gray-dark p-4 px-6 text-white">
                                {{ $t('YES') }}
                            </div>
                        </template>

                        <template v-else>
                            <div class="rounded-full bg-gray-dark p-4 px-8 text-white">
                                {{ $t('NO') }}
                            </div>
                            <div class="rounded-full p-4 px-6">
                                {{ $t('YES') }}
                            </div>
                        </template>
                    </button>
                </span>
            </div>
        </step-heading>

        <div v-if="!isAvailable" class="mr-4 flex flex-col">
            <div class="mb-[16px] text-sm font-medium uppercase text-gray-dark">
                <div class="help-content" v-html="bulkOrderNotAvailableText"></div>
            </div>
        </div>

        <div v-if="isAvailable && !isChecked" class="mr-4 flex flex-col">
            <div class="mb-[16px] text-sm font-medium uppercase text-gray-dark">
                <div class="help-content" v-html="bulkOrderText"></div>
            </div>
        </div>

        <div v-if="isAvailable && !isChecked && !canEnable" class="mr-4">
            <div class="mb-[16px] text-sm font-medium uppercase text-danger">
                {{$t('BULK_ENABLE_WARNING')}}
            </div>
        </div>

        <div v-if="isAvailable && isChecked" class="flex flex-col gap-8">
            <div class="flex flex-row items-center">
                <div class="flex flex-1 flex-col gap-1">
                    <div class="font-sans text-[14px]">
                        {{$t('UPLOAD_OWN')}}
                    </div>
                    <div class="font-sans text-[12px] help-content">
                        <span v-html="$t('UPLOAD_TEMPLATE')" />
                    </div>
                </div>

                <div class="flex-none">
                    <div class="relative h-[75px] w-[95px]">
                        <input type="file"
                            ref="upload"
                            class="absolute size-full opacity-0"
                            accept="*.csv,*.xlsx"
                            @change="onUpload($event)"
                        />
                        <div class="pointer-events-none absolute flex size-full flex-col items-center justify-center rounded bg-gray-200">
                            <span class="">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_5348_2575)">
                                        <path d="M9 16H15V10H19L12 3L5 10H9V16ZM5 18H19V20H5V18Z" fill="#333333"/>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5348_2575">
                                            <rect width="24" height="24" fill="white"/>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                            <span class="font-sans text-[12px]">
                                {{$t('UPLOAD_UPLOAD')}}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex flex-row items-center gap-2">
                <div class="h-px flex-1 bg-gray-darker"></div>
                <div class="font-caption text-[14px]">
                    {{$t('BULK_OR')}}
                </div>
                <div class="h-px flex-1 bg-gray-darker"></div>
            </div>

            <div class="flex flex-row items-center">
                <div class="flex-1 flex-sans text-darkest text-[14px]">
                    {{$t('BULK_COUNT')}}
                </div>
                <div class="flex-1">
                    <quantity-input class="mb-0" :quantity="quantity" @update-quantity="updateQuantity"/>
                </div>
            </div>

            <div class="flex flex-row items-center">
                <div class="flex-1 flex-sans text-darkest text-[14px]">
                    {{$t('BULK_EACH')}}
                </div>
            </div>

            <div class="flex flex-col gap-4 pb-4" v-auto-animate>
                <bulk-item v-for="(item, index) in groupPersonalization" :key="item.id" :item="item" :index="index" />
            </div>
        </div>
    </step-wrapper>
</template>

<style lang="scss" scoped>
    #cz3.cz3 {
        .help-content :deep(li) {
            list-style: disc;

            margin-bottom: 1rem;
        }

        .help-content :deep(a) {
            color: #333;

            text-decoration: underline;
            text-underline-offset: 2px;
        }
    }
</style>

<script>
    import { read, utils } from 'xlsx';

    import StepHeading from '../UI/headings/StepHeading.vue';
    import StepWrapper from '../wrappers/StepWrapper.vue';
    import QuantityInput from '../UI/inputs/QuantityInput.vue';
    import BulkItem from './BulkItem.vue';

    import allStores from '../../stores/all';

    export default {
        mixins: [allStores],

        components: {
            StepHeading,
            StepWrapper,
            QuantityInput,
            BulkItem,
        },

        data() {
            return {
                isChecked: false,
                canEnable: false,

                quantity: 1,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore?.viewController;
            },

            bulkOrderText() {
                if (this.viewController == null) {
                    return '';
                }

                return this.customizerStore.getTextSection('BulkOrdering', 'Information');
            },

            groupPersonalization() {
                return this.customizerStore.groupPersonalization;
            },

            isAvailable() {
                return (this.viewController?.blueprint?.custom['ridge-bulk-order-not-available'] ?? '').length === 0;
            },

            bulkOrderNotAvailableText() {
                if (this.viewController == null) {
                    return '';
                }

                const code = (this.viewController?.blueprint?.custom['ridge-bulk-order-not-available'] ?? '');

                return this.customizerStore.getTextSection('BulkOrdering', code);
            },
        },

        watch: {
            groupPersonalization: {
                handler() {
                    this.setupPersonalization();
                },
                deep: true,
            },
        },

        mounted() {
            if (this.customizerStore.targetGroupPersonalizationType != null) {
                this.canEnable = true;

                if (this.groupPersonalization == null) {
                    this.isChecked = false;
                } else {
                    this.isChecked = true;

                    this.setupPersonalization();
                }
            }
        },

        methods: {
            toggleBulkOrder() {
                if (this.canEnable) {
                    if (this.isChecked) {
                        this.isChecked = false;

                        this.customizerStore.disableGroupPersonalization();
                    } else {
                        this.isChecked = true;

                        this.customizerStore.enableGroupPersonalization();

                        this.setupPersonalization();
                    }
                } else {
                    this.customizerStore.toggleEnableBulkInformationModal();
                }
            },

            setupPersonalization() {
                this.quantity = this.groupPersonalization?.length ?? 1;
            },

            async updateQuantity(v) {
                if (v > 0) {
                    let useV = v;

                    if (useV > 20) {
                        useV = 20;
                    }

                    this.quantity = useV;

                    await this.customizerStore.updateGroupQuantity(useV);
                }
            },

            parseCsv(text) {
                const rows = [];

                const lines = text.split(/\r?\n/);

                for (const line of lines) {
                    if (!line) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const columns = [];

                    let currentField = '';
                    let insideQuotes = false;

                    for (let i = 0; i < line.length; i += 1) {
                        const character = line[i];

                        if (character === '"') {
                            // Handle escaped quote
                            if (insideQuotes && line[i + 1] === '"') {
                                currentField += '"';
                                i += 1;
                            } else {
                                insideQuotes = !insideQuotes;
                            }
                        } else if (character === ',' && !insideQuotes) {
                            columns.push(currentField);

                            currentField = '';
                        } else {
                            currentField += character;
                        }
                    }

                    columns.push(currentField);

                    rows.push(columns);
                }

                return rows;
            },

            async onUpload() {
                const uploadedFiles = this.$refs.upload.files;

                if (uploadedFiles.length > 0) {
                    const file = uploadedFiles[0];

                    const extension = file.name.split('.').pop().toLowerCase();

                    if (extension === 'xlsx') {
                        const buffer = await file.arrayBuffer();

                        const workbook = read(buffer, { type: 'array' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        const data = utils.sheet_to_json(sheet, { header: 1 });

                        let list = data.map((r) => r[0]).filter((v) => v != null && v.trim() !== '');

                        if (list.length > 0) {
                            list = list.slice(1);
                        }

                        if (list.length > 20) {
                            list = list.slice(0, 20);
                        }

                        await this.setNames(list);
                    } else if (extension === 'csv') {
                        const csvText = await file.text();

                        let rows = this.parseCsv(csvText);

                        if (rows.length > 0) {
                            // Skip header.
                            rows = rows.slice(1);
                        }

                        if (rows.length > 20) {
                            rows = rows.slice(0, 20);
                        }

                        await this.setNames(rows.map((r) => r[0]).filter((v) => v != null && v.trim() !== ''));
                    }

                    this.$refs.upload.value = null;
                }
            },

            async setNames(list) {
                if (list.length > 0) {
                    await this.customizerStore.applyGroupPersonalizationList(list);
                }
            },
        },
    };
</script>
